import React from 'react';
import { useAuth } from '../context/AuthContext';
import { PublicLayout } from '../components/landing/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-cinzel font-bold text-lg text-foreground mb-3 border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">
      {children}
    </div>
  </div>
);

const SubSection = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="font-semibold text-foreground/80 text-sm mb-2">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const PlanTable = () => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-muted/40">
          <th className="text-left px-4 py-2 font-semibold">Plan</th>
          <th className="text-left px-4 py-2 font-semibold">Prix</th>
          <th className="text-left px-4 py-2 font-semibold">Facturation</th>
          <th className="text-left px-4 py-2 font-semibold">Fonctionnalités principales</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        <tr>
          <td className="px-4 py-2 font-medium">Gratuit (Free)</td>
          <td className="px-4 py-2">0 €</td>
          <td className="px-4 py-2">–</td>
          <td className="px-4 py-2">Accès de base, campagnes limitées</td>
        </tr>
        <tr>
          <td className="px-4 py-2 font-medium">Pro</td>
          <td className="px-4 py-2">[À COMPLÉTER] €/mois</td>
          <td className="px-4 py-2">Mensuelle ou annuelle</td>
          <td className="px-4 py-2">Campagnes illimitées, outils avancés, audio, marketplace</td>
        </tr>
        <tr>
          <td className="px-4 py-2 font-medium">Guilde (Guild)</td>
          <td className="px-4 py-2">Sur devis</td>
          <td className="px-4 py-2">Annuelle</td>
          <td className="px-4 py-2">Toutes les fonctionnalités Pro + gestion multi-MJ + support dédié</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export default function CGVPage() {
  const { language } = useAuth();
  const title = language === 'fr' ? 'Conditions Générales de Vente' : 'Terms of Sale';
  const subtitle = language === 'fr' ? 'CGV — WyrdMaker' : 'ToS — WyrdMaker';

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="flex justify-center mb-4">
            <ShoppingCart className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {title}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">{subtitle}</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-border/50">
            <CardContent className="p-8">

              {/* Préambule */}
              <div className="mb-8 p-4 bg-primary/5 border border-primary/20 rounded-lg text-sm text-muted-foreground leading-relaxed">
                <p>
                  Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent les conditions dans lesquelles
                  <strong> [À COMPLÉTER — dénomination sociale]</strong> (ci-après « l'Éditeur »), propose à la vente
                  les abonnements et services payants de la Plateforme <strong>WyrdMaker</strong>.
                </p>
                <p className="mt-2">
                  Tout achat d'un abonnement ou d'un service payant implique l'acceptation sans réserve des présentes CGV, ainsi que des
                  Conditions Générales d'Utilisation (CGU). Les présentes CGV prévalent sur tout autre document.
                </p>
                <p className="mt-2">
                  Les présentes CGV sont applicables aux relations contractuelles entre l'Éditeur et tout acheteur <strong>professionnel</strong> ou <strong>consommateur</strong> (personne physique agissant à des fins n'entrant pas dans le cadre de son activité commerciale ou professionnelle), conformément aux articles L.211-1 et suivants du Code de la consommation.
                </p>
              </div>

              <Section title="Article 1 — Identification du vendeur">
                <div className="p-4 bg-muted/30 rounded-lg space-y-1">
                  <p><strong>Raison sociale :</strong> [À COMPLÉTER]</p>
                  <p><strong>Forme juridique :</strong> [À COMPLÉTER]</p>
                  <p><strong>SIRET :</strong> [À COMPLÉTER]</p>
                  <p><strong>Siège social :</strong> [À COMPLÉTER]</p>
                  <p><strong>N° TVA intracommunautaire :</strong> [À COMPLÉTER]</p>
                  <p><strong>E-mail :</strong> [À COMPLÉTER — contact@wyrdmaker.com]</p>
                </div>
              </Section>

              <Section title="Article 2 — Objet et champ d'application">
                <p>
                  Les présentes CGV ont pour objet de définir les droits et obligations des parties dans le cadre de la vente en ligne
                  d'abonnements donnant accès aux fonctionnalités premium de WyrdMaker, plateforme de gestion de campagnes
                  de jeux de rôle sur table (JDR).
                </p>
                <p>
                  Les services vendus sont des <strong>contenus numériques fournis sur support immatériel</strong> et des
                  <strong> abonnements à des services en ligne</strong>, conformément aux articles L.224-25 et suivants du Code de la consommation.
                </p>
              </Section>

              <Section title="Article 3 — Offres et tarifs">
                <SubSection title="3.1 Description des plans">
                  <p>Les offres payantes disponibles à la date de la commande sont présentées sur la page Tarifs de la Plateforme.</p>
                  <PlanTable />
                </SubSection>
                <SubSection title="3.2 Prix">
                  <p>
                    Les prix sont indiqués en euros (€), <strong>toutes taxes comprises (TTC)</strong>. Le taux de TVA applicable est celui en vigueur à la date de la commande conformément à la législation française/européenne.
                  </p>
                  <p>
                    L'Éditeur se réserve le droit de modifier ses tarifs à tout moment. Toutefois, les abonnements en cours ne sont pas affectés jusqu'à leur prochaine échéance de renouvellement.
                    L'Utilisateur sera informé de toute modification tarifaire affectant son abonnement au moins <strong>30 jours</strong> avant son entrée en vigueur.
                  </p>
                </SubSection>
                <SubSection title="3.3 Offres promotionnelles">
                  <p>Des offres promotionnelles peuvent être proposées ponctuellement. Elles sont soumises à des conditions particulières et ne sont pas cumulables sauf mention contraire.</p>
                </SubSection>
              </Section>

              <Section title="Article 4 — Processus de commande">
                <SubSection title="4.1 Passage de commande">
                  <p>Pour souscrire à un abonnement payant, l'Utilisateur doit :</p>
                  <ol className="list-decimal list-inside ml-4 space-y-1 mt-1">
                    <li>Disposer d'un compte actif sur la Plateforme</li>
                    <li>Sélectionner le plan souhaité sur la page Tarifs ou Abonnements</li>
                    <li>Choisir sa période de facturation (mensuelle ou annuelle)</li>
                    <li>Renseigner ses informations de paiement via l'interface sécurisée du prestataire de paiement</li>
                    <li>Valider sa commande en cliquant sur le bouton de confirmation</li>
                  </ol>
                </SubSection>
                <SubSection title="4.2 Confirmation de commande">
                  <p>
                    L'Éditeur adresse à l'Utilisateur, après paiement confirmé, un e-mail de confirmation récapitulant les caractéristiques de l'abonnement souscrit, le prix, les modalités d'accès et les informations relatives au droit de rétractation.
                  </p>
                  <p>
                    Le contrat de vente est réputé conclu à la date de réception de cet e-mail de confirmation.
                  </p>
                </SubSection>
              </Section>

              <Section title="Article 5 — Paiement">
                <SubSection title="5.1 Modalités de paiement">
                  <p>Le paiement s'effectue exclusivement en ligne par :</p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li><strong>Carte bancaire</strong> (Visa, Mastercard, American Express) via <strong>Stripe</strong></li>
                    <li><strong>PayPal</strong> (compte PayPal ou carte via PayPal)</li>
                  </ul>
                  <p className="mt-2">
                    Les transactions sont sécurisées par les protocoles SSL/TLS. L'Éditeur n'a pas accès aux données de carte bancaire de l'Utilisateur, lesquelles sont traitées directement par les prestataires de paiement (Stripe Inc. et PayPal Holdings, Inc.).
                  </p>
                </SubSection>
                <SubSection title="5.2 Facturation et renouvellement automatique">
                  <p>
                    Les abonnements sont à reconduction tacite. Le prélèvement est effectué automatiquement à chaque échéance (mensuelle ou annuelle) selon les informations de paiement enregistrées.
                    L'Utilisateur recevra un rappel par e-mail avant chaque renouvellement annuel.
                  </p>
                </SubSection>
                <SubSection title="5.3 Défaut de paiement">
                  <p>
                    En cas d'échec du paiement, l'Éditeur se réserve le droit de suspendre l'accès aux fonctionnalités premium dans l'attente de la régularisation. Des frais de retard peuvent être applicables aux achats professionnels conformément aux articles L.441-10 et suivants du Code de commerce (taux légal majoré de 10 points).
                  </p>
                </SubSection>
              </Section>

              <Section title="Article 6 — Droit de rétractation (Consommateurs)">
                <SubSection title="6.1 Principe">
                  <p>
                    Conformément aux articles <strong>L.221-18 et suivants du Code de la consommation</strong>, le consommateur dispose d'un délai de
                    <strong> 14 jours calendaires</strong> à compter de la souscription de l'abonnement pour exercer son droit de rétractation, sans avoir à justifier de motifs ni payer de pénalités.
                  </p>
                </SubSection>
                <SubSection title="6.2 Renonciation au droit de rétractation pour contenu numérique">
                  <p>
                    Conformément à l'article <strong>L.221-28 12° du Code de la consommation</strong>, le droit de rétractation ne peut être exercé pour les contenus numériques fournis sur un support immatériel dont l'exécution a commencé avec l'accord préalable exprès du consommateur et sa renonciation expresse à son droit de rétractation.
                  </p>
                  <p>
                    En accédant immédiatement aux fonctionnalités premium après validation de sa commande, l'Utilisateur reconnaît expressément renoncer à son droit de rétractation pour la période déjà consommée.
                    L'accès au service ne commence qu'avec l'accord exprès de l'Utilisateur, qui en est informé lors du processus de commande.
                  </p>
                </SubSection>
                <SubSection title="6.3 Exercice du droit de rétractation">
                  <p>Pour exercer ce droit dans le délai imparti, l'Utilisateur doit notifier sa décision par :</p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>E-mail à <strong>[À COMPLÉTER — email de contact]</strong> avec mention « Rétractation WyrdMaker »</li>
                    <li>Formulaire disponible dans son espace Abonnement sur la Plateforme</li>
                    <li>Courrier recommandé avec accusé de réception à l'adresse du siège social</li>
                  </ul>
                  <p className="mt-2">
                    En cas de rétractation valide, l'Éditeur remboursera le montant intégral dans un délai de <strong>14 jours</strong> suivant la notification, via le même moyen de paiement utilisé lors de la commande.
                  </p>
                </SubSection>
              </Section>

              <Section title="Article 7 — Résiliation et annulation de l'abonnement">
                <SubSection title="7.1 Résiliation par l'Utilisateur">
                  <p>
                    L'Utilisateur peut résilier son abonnement à tout moment depuis son espace « Abonnement » ou en contactant le support.
                    La résiliation prend effet à la fin de la période de facturation en cours. Aucun remboursement prorata temporis n'est accordé pour les abonnements mensuels.
                  </p>
                  <p>
                    Pour les abonnements annuels, si la résiliation intervient dans les <strong>30 jours</strong> suivant la date de renouvellement, un remboursement prorata temporis peut être accordé sur demande motivée, hors cas de renonciation au droit de rétractation.
                  </p>
                </SubSection>
                <SubSection title="7.2 Résiliation par l'Éditeur">
                  <p>
                    L'Éditeur peut résilier l'abonnement d'un Utilisateur en cas de violation grave des CGU ou des CGV, après mise en demeure restée sans effet sous 48 heures. Le cas échéant, le prorata du mois ou de l'année non consommé sera remboursé.
                  </p>
                </SubSection>
                <SubSection title="7.3 Arrêt du service">
                  <p>
                    En cas d'arrêt définitif de la Plateforme, l'Éditeur s'engage à en informer les Utilisateurs au moins <strong>90 jours</strong> à l'avance par e-mail et à rembourser le prorata des abonnements payés non consommés.
                  </p>
                </SubSection>
              </Section>

              <Section title="Article 8 — Garanties légales">
                <p>
                  Conformément aux articles <strong>L.217-1 et suivants du Code de la consommation</strong> (garantie légale de conformité) et aux articles
                  <strong> 1641 et suivants du Code civil</strong> (garantie légale contre les vices cachés), l'Éditeur garantit les services vendus contre tout défaut de conformité.
                </p>
                <p>
                  En cas de défaut de conformité d'un service numérique, le consommateur peut demander la mise en conformité du service ou, à défaut, la réduction du prix ou la résolution du contrat dans les conditions prévues par la loi.
                </p>
              </Section>

              <Section title="Article 9 — Responsabilité">
                <p>
                  L'Éditeur est soumis à une obligation de <strong>moyens</strong> et non de résultat quant à la disponibilité et au bon fonctionnement de la Plateforme.
                  En cas de dommage avéré imputable à l'Éditeur, sa responsabilité est limitée au montant des sommes effectivement payées par l'Utilisateur au cours des
                  <strong> 12 derniers mois</strong> précédant le fait générateur.
                </p>
                <p>
                  L'Éditeur ne saurait être tenu responsable des dommages indirects, incluant notamment la perte de données, la perte de chiffre d'affaires, la perte d'une chance ou d'un profit.
                </p>
              </Section>

              <Section title="Article 10 — Données personnelles et facturation">
                <p>
                  Les données personnelles collectées lors de la commande (identité, adresse e-mail, adresse de facturation) sont nécessaires au traitement de la commande et à la facturation.
                  Elles sont conservées pendant la durée légale applicable (10 ans pour les documents comptables conformément au Code de commerce).
                </p>
                <p>
                  Les données de paiement (numéro de carte, etc.) sont traitées exclusivement par <strong>Stripe</strong> et/ou <strong>PayPal</strong> et ne sont pas conservées par l'Éditeur.
                </p>
              </Section>

              <Section title="Article 11 — Médiation et règlement des litiges">
                <p>
                  En cas de litige relatif à une commande, l'Utilisateur doit adresser une réclamation écrite au service client de l'Éditeur.
                  En l'absence de réponse satisfaisante dans un délai de <strong>60 jours</strong>, tout consommateur peut recourir gratuitement à un médiateur de la consommation.
                </p>
                <p>
                  Médiateur désigné : <strong>[À COMPLÉTER — Nom du médiateur / organisme de médiation agréé]</strong><br />
                  Site : <strong>[À COMPLÉTER]</strong>
                </p>
                <p>
                  Plateforme européenne de résolution en ligne des litiges : <strong>https://ec.europa.eu/consumers/odr</strong>
                </p>
              </Section>

              <Section title="Article 12 — Droit applicable">
                <p>
                  Les présentes CGV sont soumises au droit français. Pour les consommateurs résidant dans l'Union Européenne, les dispositions plus favorables de la législation de leur pays de résidence peuvent s'appliquer.
                </p>
                <p>
                  Tout litige relatif à l'interprétation ou l'exécution des présentes CGV sera soumis, à défaut de résolution amiable, à la compétence exclusive des tribunaux du ressort du siège social de l'Éditeur.
                </p>
              </Section>

              <p className="text-xs text-muted-foreground/60 mt-8 pt-4 border-t border-border">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

            </CardContent>
          </Card>
        </div>
      </section>
    </PublicLayout>
  );
}
